import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import DataStateError from "../components/DataStateError";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertTriangle, CalendarClock, Wallet, PauseCircle, FileWarning,
  RefreshCw, Check, Ban, ShieldAlert,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era dataIT).
import { formatDate } from "../lib/format";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function OperationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // Senza questo, un guasto di rete faceva scrivere "Nessuna polizza scaduta
  // senza rinnovo. Ottimo." — cioe' il contrario della verita'.
  const [loadError, setLoadError] = useState(false);
  const [days, setDays] = useState("30");
  const [risk, setRisk] = useState(null);
  const [canc, setCanc] = useState(null);

  useEffect(() => {
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const carica = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`/api/insights/at-risk?days=${days}`),
        axios.get(`/api/insights/cancellations?days=120`),
      ]);
      setRisk(r.data);
      setCanc(c.data);
    } catch (e) {
      setLoadError(true);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const segnaDisdetta = async (policyId, inviata) => {
    try {
      await axios.post(`/api/insights/cancellations/${policyId}/mark?sent=${inviata}`);
      toast.success(inviata ? "Disdetta segnata" : "Segnalazione annullata");
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione non riuscita");
    }
  };

  const s = risk?.summary || {};

  const Riquadro = ({ icon: Icon, titolo, valore, colore, nota }) => (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{titolo}</p>
        <Icon className={`h-4 w-4 ${colore}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${colore}`}>{valore}</p>
      {nota ? <p className="text-[11px] text-muted-foreground mt-0.5">{nota}</p> : null}
    </Card>
  );

  const Tabella = ({ righe, vuoto, mostraMotivo = true }) => {
    if (!righe || righe.length === 0) {
      return <p className="text-sm text-muted-foreground p-6 text-center">{vuoto}</p>;
    }
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Polizza</TableHead>
              <TableHead>Compagnia</TableHead>
              <TableHead>Data</TableHead>
              {mostraMotivo && <TableHead>Motivo</TableHead>}
              <TableHead className="text-right">Premio</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {righe.map((r, i) => (
              <TableRow
                key={r.policy_id || i}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => r.policy_id && navigate(`/policies/${r.policy_id}`)}
              >
                <TableCell className="font-medium">
                  <div className="truncate max-w-[190px]">{r.client_name}</div>
                  {r.license_plate ? (
                    <span className="text-xs font-mono text-muted-foreground">{r.license_plate}</span>
                  ) : null}
                </TableCell>
                <TableCell className="text-sm font-mono">{r.policy_number}</TableCell>
                <TableCell className="text-sm">{r.company}</TableCell>
                <TableCell className="text-sm">{formatDate(r.data, "-")}</TableCell>
                {mostraMotivo && (
                  <TableCell>
                    <Badge
                      variant={r.motivo?.includes("SCADUTA") ? "destructive" : "secondary"}
                      className="text-[10px]"
                    >
                      {r.motivo}
                    </Badge>
                  </TableCell>
                )}
                <TableCell className="text-right font-mono">{fmt(r.premium)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="operations-page-errore">
        <PageHeader titolo="Da gestire" />
        <Card className="bg-card border-border">
          <DataStateError
            titolo="Non riesco a leggere le cose da gestire"
            messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto. Non fidarti di quello che vedi finche' non ricarica."
            onRetry={carica}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <PageHeader titolo="Da gestire" descrizione="Cosa richiede attenzione adesso" />
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">Prossimi 15 giorni</SelectItem>
              <SelectItem value="30">Prossimi 30 giorni</SelectItem>
              <SelectItem value="60">Prossimi 60 giorni</SelectItem>
              <SelectItem value="90">Prossimi 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={carica}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Riquadro
          icon={ShieldAlert}
          titolo="Scadute non rinnovate"
          valore={s.scadute_non_rinnovate || 0}
          colore="text-red-600"
          nota="ultimi 60 giorni"
        />
        <Riquadro
          icon={CalendarClock}
          titolo="In scadenza"
          valore={s.scadenze_in_arrivo || 0}
          colore="text-amber-600"
          nota={fmt(s.premio_a_rischio) + " a rischio"}
        />
        <Riquadro
          icon={Wallet}
          titolo="Rate da incassare"
          valore={s.rate_da_incassare || 0}
          colore="text-blue-600"
          nota="semestrali scadute"
        />
        <Riquadro
          icon={PauseCircle}
          titolo="Sospese da riprendere"
          valore={s.sospese_da_riprendere || 0}
          colore="text-purple-600"
        />
      </div>

      <Tabs defaultValue="scadute">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="scadute">
            Scadute ({risk?.scadute_non_rinnovate?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="scadenze">
            In scadenza ({risk?.scadenze_in_arrivo?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="rate">
            Rate ({(risk?.rate_da_incassare?.length || 0) + (risk?.rate_in_arrivo?.length || 0)})
          </TabsTrigger>
          <TabsTrigger value="sospese">
            Sospese ({risk?.sospese_da_riprendere?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="disdette">
            Disdette ({canc?.count || 0})
          </TabsTrigger>
          <TabsTrigger value="documenti">
            Documenti ({risk?.clienti_senza_documento?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scadute">
          <Card className="border-red-300/50">
            <div className="p-3 border-b bg-red-50/50 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">
                Scadute e mai rinnovate — il cliente potrebbe essere scoperto
              </span>
            </div>
            <Tabella
              righe={risk?.scadute_non_rinnovate}
              vuoto="Nessuna polizza scaduta senza rinnovo. Ottimo."
            />
          </Card>
        </TabsContent>

        <TabsContent value="scadenze">
          <Card>
            <Tabella
              righe={risk?.scadenze_in_arrivo}
              vuoto="Nessuna scadenza nel periodo selezionato."
            />
          </Card>
        </TabsContent>

        <TabsContent value="rate">
          <Card className="mb-3">
            <div className="p-3 border-b bg-blue-50/50 text-sm font-medium text-blue-800">
              Rate semestrali già scadute e non incassate
            </div>
            <Tabella
              righe={risk?.rate_da_incassare}
              vuoto="Nessuna rata arretrata."
            />
          </Card>
          <Card>
            <div className="p-3 border-b text-sm font-medium">
              Rate in arrivo nel periodo
            </div>
            <Tabella
              righe={risk?.rate_in_arrivo}
              vuoto="Nessuna rata in arrivo."
            />
          </Card>
        </TabsContent>

        <TabsContent value="sospese">
          <Card>
            <Tabella
              righe={risk?.sospese_da_riprendere}
              vuoto="Nessuna sospensione da riprendere."
            />
          </Card>
        </TabsContent>

        <TabsContent value="disdette">
          <Card className="border-amber-300/50">
            <div className="p-3 border-b bg-amber-50/50">
              <p className="text-sm font-medium text-amber-900">
                Registro disdette — solo polizze non veicolo
              </p>
              <p className="text-xs text-muted-foreground">
                {canc?.urgenti || 0} urgenti, {canc?.scaduti || 0} con termine già passato
              </p>
            </div>
            {!canc?.items?.length ? (
              <p className="text-sm text-muted-foreground p-6 text-center">
                Nessun termine di disdetta in avvicinamento.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Polizza</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Termine disdetta</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azione</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {canc.items.map((r) => (
                      <TableRow key={r.policy_id}>
                        <TableCell className="font-medium">
                          <div className="truncate max-w-[180px]">{r.client_name}</div>
                          <span className="text-xs text-muted-foreground">{r.company}</span>
                        </TableCell>
                        <TableCell className="text-sm font-mono">{r.policy_number}</TableCell>
                        <TableCell className="text-sm">{formatDate(r.expiry_date, "-")}</TableCell>
                        <TableCell className="text-sm">
                          {formatDate(r.deadline, "-")}
                          <div className="text-[11px] text-muted-foreground">
                            {r.days_left >= 0
                              ? `tra ${r.days_left} giorni`
                              : `passato da ${Math.abs(r.days_left)} giorni`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              r.cancellation_sent ? "default"
                                : r.days_left < 0 ? "destructive"
                                : r.days_left <= 7 ? "destructive"
                                : "secondary"
                            }
                            className="text-[10px]"
                          >
                            {r.stato}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.cancellation_sent ? (
                            <Button size="sm" variant="ghost"
                              onClick={() => segnaDisdetta(r.policy_id, false)}>
                              <Ban className="h-3.5 w-3.5 mr-1" /> Annulla
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline"
                              onClick={() => segnaDisdetta(r.policy_id, true)}>
                              <Check className="h-3.5 w-3.5 mr-1" /> Inviata
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documenti">
          <Card>
            <div className="p-3 border-b flex items-center gap-2">
              <FileWarning className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                Clienti con polizze attive ma senza documento d'identità
              </span>
            </div>
            {!risk?.clienti_senza_documento?.length ? (
              <p className="text-sm text-muted-foreground p-6 text-center">
                Tutti i clienti attivi hanno un documento.
              </p>
            ) : (
              <div className="p-3 flex flex-wrap gap-2">
                {risk.clienti_senza_documento.map((c) => (
                  <Button
                    key={c.client_id}
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/clients/${c.client_id}`)}
                  >
                    {c.client_name}
                  </Button>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
